import PropTypes from 'prop-types';
import React from 'react';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { ProfileService, AccountService } from '@sm360/widget-commons-general';

import InputDropDown from 'commons-forms/components/InputDropDown/InputDropDown.jsx';
import InputText from 'commons-forms/components/InputText/InputText.jsx';
import InputZipCode from 'commons-forms/components/InputZipCode/InputZipCode.jsx';

import GroupResponseService from 'commons-forms/services/GroupResponseService/GroupResponseService.js';
import ValidationService from 'commons-forms/services/ValidationService/ValidationService.js';

import FIELD_LENGTH from 'commons-forms/constants/fieldLength.js';

class AddressGroup extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            formValidation: {
                address: ValidationService.validateIsNotEmpty(props.values.address),
                city: ValidationService.validateIsNotEmpty(props.values.city),
                postalCode: ValidationService.validateZipCode(props.values.postalCode, props.country),
                province: ValidationService.validateIsNotEmpty(props.values.province),
            },
            profile: {},
        };

        this.handleDropdownChange = this.handleDropdownChange.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleZipCodeChange = this.handleZipCodeChange.bind(this);
        this.sendResponse = this.sendResponse.bind(this);
    }

    componentDidMount() {
        // We need make an api call here so as to get all the information needed to prepopulate our form.
        // We COULD solely rely on props theoretically, but until we have a single redux store,
        // we are better off double checking here...
        // TODO: Remove this Api call.
        if (AccountService.isAuthenticated()) {
            ProfileService.getLoggedAccountProfile().then((profile) => {
                if (profile.province) {
                    profile.province = profile.province.toLowerCase();
                }
                this.setState({ profile }, this.forceFormValidation);
            });
        }
    }

    forceFormValidation() {
        this.setState(
            (oldState) => ({
                formValidation: {
                    address: ValidationService.validateIsNotEmpty(oldState.profile.address),
                    city: ValidationService.validateIsNotEmpty(oldState.profile.city),
                    postalCode: ValidationService.validateIsNotEmpty(oldState.profile.postalCode),
                    province: ValidationService.validateIsNotEmpty(oldState.profile.province),
                },
            }),
            () => {
                const groupResponse = this.createGroupResponse();
                this.props.onChange(groupResponse);
            }
        );
    }

    handleDropdownChange(option) {
        const { profile, formValidation } = this.state;

        profile.province = option.value;
        formValidation.province = true;
        this.setState(
            {
                profile,
                formValidation,
            },
            this.sendResponse()
        );
    }

    handleInputChange(value, name) {
        if (name === 'address2') {
            this.setState({
                profile: {
                    ...this.state.profile,
                    address2: value,
                },
            });
        } else {
            this.setState({
                formValidation: {
                    ...this.state.formValidation,
                    [name]: ValidationService.validateIsNotEmpty(value),
                },
                profile: {
                    ...this.state.profile,
                    [name]: value,
                },
            });
        }
    }

    handleZipCodeChange(zipcodeResponse) {
        const { profile, formValidation } = this.state;

        profile.postalCode = zipcodeResponse.value;
        formValidation.postalCode = zipcodeResponse.validation;

        this.setState({
            profile,
            formValidation,
        });
    }

    handleFormSubmission() {
        if (AccountService.isAuthenticated() && ValidationService.validateForm(this.state.formValidation)) {
            ProfileService.updateAccountProfile(this.state.profile);
        }
    }

    sendResponse() {
        const groupResponse = this.createGroupResponse();
        // Not to update info if its address delivery or restricted to update info
        if (!this.props.isAddressDelivery && !this.props.isRestrictUpdateInfo) this.handleFormSubmission();
        this.props.onChange(groupResponse);
    }

    createGroupResponse() {
        const areFieldsValid = ValidationService.validateForm(this.state.formValidation);

        const inputs = {
            address: this.state.profile.address,
            address2: this.state.profile.address2,
            city: this.state.profile.city,
            postalCode: this.state.profile.postalCode,
            province: this.state.profile.province,
        };

        const groupResponse = GroupResponseService.createResponse(
            areFieldsValid,
            inputs,
            'error.group.address',
            null,
            'addressGroup'
        );

        return groupResponse;
    }

    render() {
        return (
            <div>
                <InputText
                    type="text"
                    name="address"
                    classNames={['styled-placeholder full']}
                    label={this.props.labels.address}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.ADRESS')}
                    value={this.state.profile.address}
                    onBlur={this.sendResponse}
                    isValid={this.state.formValidation.address}
                    isRequired
                    valueChange={this.handleInputChange}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    maxLength={FIELD_LENGTH.ADDRESS_MAX}
                />

                <InputText
                    type="text"
                    name="address2"
                    classNames={['styled-placeholder full']}
                    label={this.props.labels.address2}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.ADRESS_2')}
                    value={this.state.profile.address2}
                    onBlur={this.sendResponse}
                    isValid
                    valueChange={this.handleInputChange}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    maxLength={FIELD_LENGTH.ADDRESS_MAX}
                />

                <InputZipCode
                    classNames={['styled-placeholder third']}
                    label={this.props.labels.postalCode}
                    errorLabel={
                        this.props.country === 'us'
                            ? LocaleService.t('COMMONS_FORMS.ERRORS.ZIP_CODE')
                            : LocaleService.t('COMMONS_FORMS.ERRORS.POSTAL_CODE')
                    }
                    value={this.state.profile.postalCode}
                    onBlur={this.sendResponse}
                    isValid={this.state.formValidation.postalCode}
                    isRequired
                    onChange={this.handleZipCodeChange}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    country={this.props.country}
                />

                <InputText
                    type="text"
                    name="city"
                    classNames={['styled-placeholder third']}
                    label={this.props.labels.city}
                    errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.CITY')}
                    value={this.state.profile.city}
                    onBlur={this.sendResponse}
                    isValid={this.state.formValidation.city}
                    isRequired
                    valueChange={this.handleInputChange}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                    maxLength={FIELD_LENGTH.CITY_MAX}
                />

                <InputDropDown
                    name="province"
                    classNames={['input-dropdown third']}
                    options={this.props.provinces}
                    placeholder={this.props.labels.province}
                    errorLabel={
                        this.props.country === 'us'
                            ? LocaleService.t('COMMONS_FORMS.ERRORS.STATE')
                            : LocaleService.t('COMMONS_FORMS.ERRORS.PROVINCE')
                    }
                    value={this.state.profile.province}
                    isRequired
                    valueChange={this.handleDropdownChange}
                    hasBeenSubmit={this.props.hasBeenSubmit}
                />
            </div>
        );
    }
}

AddressGroup.defaultProps = {
    values: {
        address: '',
        address2: '',
        city: '',
        postalCode: '',
        province: '',
    },
    hasBeenSubmit: false,
    isAddressDelivery: false,
    isRestrictUpdateInfo: false,
    country: 'ca',
};

AddressGroup.propTypes = {
    labels: PropTypes.shape({
        address: PropTypes.string.isRequired,
        address2: PropTypes.string.isRequired,
        city: PropTypes.string.isRequired,
        postalCode: PropTypes.string.isRequired,
        province: PropTypes.string.isRequired,
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    provinces: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    hasBeenSubmit: PropTypes.bool,
    values: PropTypes.shape({
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        postalCode: PropTypes.string,
        province: PropTypes.string,
    }),
    isAddressDelivery: PropTypes.bool,
    isRestrictUpdateInfo: PropTypes.bool,
    country: PropTypes.string,
};

AddressGroup.displayName = 'AddressGroup';

export default AddressGroup;
